import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, RefreshCw, UserCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import shiftApi, { Shift } from '../services/shiftApi';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const ShiftManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [changingShift, setChangingShift] = useState<Shift | null>(null);
  const [newEmployeeId, setNewEmployeeId] = useState<number>(0);
  const [employees, setEmployees] = useState<any[]>([]);

  // Get all active shifts
  const { data: activeShiftsData, isLoading } = useQuery({
    queryKey: ['active-shifts'],
    queryFn: () => shiftApi.getActiveShifts(),
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const activeShifts: Shift[] = activeShiftsData?.shifts || [];

  // Fetch employees for reassignment
  React.useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await apiService.get('/employees');
        setEmployees(response.employees || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Admin end shift mutation
  const adminEndShiftMutation = useMutation({
    mutationFn: (data: { shiftId: number; newEmployeeId?: number }) =>
      shiftApi.adminEndShift(data.shiftId, {
        newEmployeeId: data.newEmployeeId,
        machineIds: [],
        machineGroupIds: []
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-shifts'] });
      toast.success('Shift changed successfully');
      setChangingShift(null);
      setNewEmployeeId(0);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to change shift');
    }
  });

  const handleChangeEmployee = (shift: Shift) => {
    setChangingShift(shift);
    setNewEmployeeId(0);
  };

  const handleConfirmChange = () => {
    if (changingShift && newEmployeeId > 0) {
      adminEndShiftMutation.mutate({
        shiftId: changingShift.id,
        newEmployeeId: newEmployeeId
      });
    } else if (changingShift) {
      // Just end the shift without reassigning
      adminEndShiftMutation.mutate({
        shiftId: changingShift.id
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (start: string) => {
    const startTime = new Date(start).getTime();
    const now = Date.now();
    const hours = Math.floor((now - startTime) / (1000 * 60 * 60));
    const minutes = Math.floor(((now - startTime) % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (user?.role !== 'ADMIN' && user?.role !== 'SUPERADMIN') {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-600">Access denied. Admin permissions required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Users className="w-8 h-8" />
          Shift Management
        </h1>
        <p className="text-gray-600">Monitor and manage active employee shifts</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : activeShifts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <UserCheck className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-medium text-gray-900 mb-2">No Active Shifts</h2>
          <p className="text-gray-600">There are currently no active employee shifts.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activeShifts.map((shift) => (
            <div key={shift.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {shift.employee.user.name}
                  </h3>
                  <p className="text-sm text-gray-600">{shift.employee.user.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">
                    Started: {formatDate(shift.startedAt)}
                  </p>
                  <p className="text-sm text-gray-600">
                    Duration: {formatDuration(shift.startedAt)}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Assigned Machines/Lines:</h4>
                <div className="flex flex-wrap gap-2">
                  {shift.machines
                    .filter(m => !m.unassignedAt)
                    .map((machine) => (
                      <span
                        key={machine.id}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {machine.machine
                          ? `Machine: ${machine.machine.number}`
                          : machine.machineGroup
                          ? `Line: ${machine.machineGroup.name}`
                          : 'Unknown'}
                      </span>
                    ))}
                </div>
              </div>

              {shift.lockoutUntil && new Date(shift.lockoutUntil) > new Date() && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    Lockout until: {formatDate(shift.lockoutUntil)}
                  </p>
                </div>
              )}

              <button
                onClick={() => handleChangeEmployee(shift)}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Change Employee
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Change Employee Modal */}
      {changingShift && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Change Employee for Shift
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Current employee: {changingShift.employee.user.name}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Employee (optional - leave empty to just end shift)
              </label>
              <select
                value={newEmployeeId}
                onChange={(e) => setNewEmployeeId(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value={0}>None (End shift only)</option>
                {employees
                  .filter((emp: any) => emp.id !== changingShift.employeeId)
                  .map((emp: any) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.user.name} ({emp.user.email})
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setChangingShift(null);
                  setNewEmployeeId(0);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmChange}
                disabled={adminEndShiftMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-300"
              >
                {adminEndShiftMutation.isPending ? 'Processing...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftManagement;

